import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { ErrorTypeEnum } from '../../../../main/constants/error-type.ts';
import { TrackingEventEnum } from '../../../../main/constants/tracking-event.ts';
import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { getAwsAuthUtil } from '../../../../main/utils/aws/aws-auth.util.ts';
import { getLocaleUtil } from '../../../../main/utils/locale.util.ts';
import { getSentryUtil } from '../../../../main/utils/monitoring/sentry.util.ts';
import { getToasterUtil } from '../../../../main/utils/toaster.util.ts';
import { getTrackerUtil } from '../../../../main/utils/trackers/tracker.util.ts';

import { ChangePasswordFormSchema } from '../../../types/accounts-validation.types.ts';

import { ACCOUNTS_SLICE_NAME, AccountsSliceState } from '../accounts.slice.types.ts';

interface Props {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const THUNK_NAME = `${ACCOUNTS_SLICE_NAME}/changePassword`;

const ERROR_MESSAGES: Record<string, string> = {
  NotAuthorizedException: getLocaleUtil().select('accounts:change_password_incorrect_password'),
};
const DEFAULT_ERROR_MESSAGE = getLocaleUtil().select('accounts:change_password_failed');

function handleError(error: Error, errorMessage: string = DEFAULT_ERROR_MESSAGE): void {
  getToasterUtil().error(errorMessage);

  getSentryUtil().trackError(error, {
    thunk: THUNK_NAME,
    errorType: ErrorTypeEnum.CHANGE_PASSWORD_ERROR,
  });
  getTrackerUtil().track({
    event: TrackingEventEnum.PASSWORD_CHANGE_FAILED,
  });
}

export const changePasswordThunk = createAsyncThunk<void, Props, ThunkApiConfigType>(THUNK_NAME, async (props) => {
  const parsedInput = ChangePasswordFormSchema.safeParse(props);
  if (!parsedInput.success) {
    const error = new Error(DEFAULT_ERROR_MESSAGE);
    handleError(error);
    throw error;
  }

  const { currentPassword, newPassword } = parsedInput.data;

  try {
    await getAwsAuthUtil().changePasword(currentPassword, newPassword);

    getToasterUtil().success(getLocaleUtil().select('accounts:change_password_success'));
    getTrackerUtil().track({
      event: TrackingEventEnum.PASSWORD_CHANGE_FAILED,
    });
  } catch (err: unknown) {
    const error = err as Error;
    const errorMessage = ERROR_MESSAGES[error.name] || error.message || DEFAULT_ERROR_MESSAGE;
    handleError(error, errorMessage);
    throw error;
  }
});

export const changePasswordThunkExtraReducers = (builder: ActionReducerMapBuilder<AccountsSliceState>): void => {
  builder.addCase(changePasswordThunk.pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(changePasswordThunk.fulfilled, (state) => {
    state.isLoading = false;
  });

  builder.addCase(changePasswordThunk.rejected, (state) => {
    state.isLoading = false;
  });
};
